import type { CardTypeEnum } from '@/components/Common/Dashboard/CardTypeEnum'
import { EntityList } from '@/types/Entity'

export type Dashboard = {
  id: string
  cards: {
    NEWS: DashboardCard
    RESUMES: [DashboardCard]
    TASKS: [DashboardCard]
    ESSENTIALS: [DashboardCard]
    ENTITIES: DashboardCard[]
    /** Score global + niveaux, exposé avec la nouvelle home (évite un appel /dashboard/scores) */
    SCORES?: { global: number; levels: Record<string, UserEntityLevel> }
  }
}

export enum TypeCardDashboard {
  NEWS = 'news',
  RESUMES = 'resumes',
  TASKS = 'tasks',
  ESSENTIALS = 'essentials',
  ENTITIES = 'entities',
}

export type UserEntityLevel = 'BEGINNER' | 'INTERMEDIATE' | 'EXPERT'

export type DashboardScores = {
  userId: number
  pharmacyId: number | null
  profile: 'TITU' | 'TEAM' | null
  scores: Record<string, { total: number; breakdown: Record<string, number> }>
  levels: Record<string, UserEntityLevel>
  computedAt: string | null
}

export type DashboardCard = {
  entity: EntityList
  data: DashboardCardData
  metadata: Record<string, any>
  typeCardDashboard?: TypeCardDashboard
}

export type DashboardCardData =
  | InfoCardData
  | StatsCardData
  | NewsContainerCardData
  | SoundCampaignsCardData
  | DiscountCardData
  | InsightSurveyListCardData
  | StorePromotionsCardData
  | StoreBagCampaignData
  | ChartCardData
  | ListCardData
  | MysteryVisitCardData
  | EntityOverviewCardData

export interface CardLink {
  type: 'INTERNAL' | 'EXTERNAL' | 'COMPONENT'
  label: string
  uri: string
  queryParams?: Record<string, string>
}

export interface InfoCardData {
  cardType: CardTypeEnum.INFO
  title: string
  titleLogo: string
  value: string
  logoStore: string
  description: string
  descriptionAsTooltip?: boolean
  descriptionSecondaryHtml?: string
  imageUrl: string
  link?: CardLink
  discriminant: string
  data: Record<string, any>
}

export interface StatsCardData {
  cardType: CardTypeEnum.STATS
  title: string
  /** Ce que compte le chiffre, en infobulle. Prime sur `statsDTO.previousValue` utilisé à cet effet. */
  description?: string
  statsDTO: {
    currentValue: number
    previousValue: number
    nextValue: number
    progressValue: number
    labelValue: string
  }
  link: CardLink
}

export interface NewsContainerCardData {
  cardType: CardTypeEnum.NEWS_CONTAINER
  banners: {
    title: string
    position: number
    showOn: Array<string | EntityList>
    description: string
    content: string
    url: string
  }[]
}

export interface SoundCampaignsCardData {
  cardType: CardTypeEnum.SOUND_CAMPAIGNS
  title: string
  campaigns: {
    title: string
    currentBroadcast: string
    goalBroadcast: string
    progress: number
  }[]
  link: CardLink
}

export interface DiscountCardData {
  cardType: CardTypeEnum.DISCOUNT
  title: string
  description: string
  discountCode: string
  link: CardLink
}

export interface InsightSurveyListCardData {
  cardType: CardTypeEnum.INSIGHT_SURVEY_LIST
  title: string
  tacheList: {
    id: number
    programName: string
    title: string
  }[]
  link: CardLink
}

export interface StorePromotionsCardData {
  cardType: CardTypeEnum.STORE_PROMOTIONS
  title: string
  promotions: {
    title: string
    startDate: number
    endDate: number
  }[]
  link: CardLink
}

export interface StoreBagCampaignData {
  cardType: CardTypeEnum.STORE_BAG_CAMPAIGN
  title: string
  options: {
    id: string | number
    label: string
    value: string | number
    isEnabled?: boolean
    orderIdStore?: string | null
  }[]
  buttonText: string
  buttonEnabled: boolean
  onClickAction?: string
  orderIdStore?: string | null
  orderIdStatusStore?: string | null
  link: CardLink
}

export interface ChartCardData {
  cardType: CardTypeEnum.CHART
  title: string
  value: number
  isValuePercent: boolean
  valueProgress: number
  description: string
  chart: {
    type: 'DOUGHNUT'
    values: {
      label: string
      number: number
      color: string
      displayLabel: boolean
    }[]
  }
}

export interface ListCardData {
  cardType: CardTypeEnum.LIST
  title: string
  description: string
  listData: {
    label: string
    number: number
    color: string
  }[]
}

/** Action à mener (liste numérotée « Vos prochaines actions ») */
export interface EntityOverviewAction {
  label: string
  urgent: boolean
  /** Redirection ciblée au clic (ex : planning Sound) — sinon CTA principal de la carte */
  link: CardLink | null
}

/** Raccourci chiffré (grille de stats) */
export interface EntityOverviewShortcut {
  label: string
  value: string
  meta: string | null
  link: CardLink | null
}

export interface EntityOverviewCardData {
  cardType: CardTypeEnum.ENTITY_OVERVIEW
  entityKey: EntityList
  level: UserEntityLevel
  score: number | null
  nextLevelScore: number | null
  progressToNextLevel: number | null
  headline: string
  description: string | null
  subText: string | null
  ctas: CardLink[]
  /** Contrat explicite : actions = liste numérotée, shortcuts = grille chiffrée */
  actions: EntityOverviewAction[]
  shortcuts: EntityOverviewShortcut[]
  locked: boolean
  /** Logo custom (ex : centrale du groupement côté CS-Cart) — remplace le logo menu */
  logoUrl: string | null
  /** Visuel d'illustration (ex : carte Nos Apps : téléphone + QR code) */
  imageUrl: string | null
  /** Variante de rendu (ex : APPS_DOWNLOAD, CARE_ACTIVE, CARE_INACTIVE) */
  discriminant: string | null
}

export interface MysteryVisitCardData {
  cardType: CardTypeEnum.MYSTERY_VISIT
  title: string
  result: string
  completedAt: Date
  anomaly: string
  nextVisit: {
    id: string
    startAt: Date
    endAt: Date
  } | null
}
